
import { Component, Provide, Prop, Mixins } from "vue-property-decorator";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import ProvisioningMixin from "@/mixins/provisioningMixin";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { configurationsFilters } from "@/data/filters/provisioning";
import { configurationsSorters } from "@/data/sorters/provisioning";
import { Sorters } from "@/data/table";
import { ActionTypes } from "@/data/enums";
import { ProvisionCategoryCodes } from "@/modules/web_hosting/data/enums";

// Typings
import {
  IProvisionConfigurationsProviderData,
  IProvisionConfigurationsProviderMethods
} from "@/models/providers/provisionConfigurations";
import { IProvisionConfiguration } from "@/models/provisioning";
import { ITableConfig, IFilter, ISorter } from "@/models/tables";

@Component
export default class ProvisionConfigurationsProvider extends Mixins(
  FilteringSortingTableStore,
  ProvisioningMixin
) {
  // Props

  @Prop({ default: null })
  public configurationType!: null;

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Variables

  public dataModule: string = DataModules.PROVISIONING_CONFIGURATIONS;

  public filters: IFilter[] = configurationsFilters;
  public sorters: ISorter[] = configurationsSorters;

  public defaults: ITableConfig = {
    limit: 20,
    sort: {
      field: "name",
      direction: Sorters.ASCENDING
    }
  };

  public loading = true;
  public isReloading = false;

  // Lifecycle methods

  public async created() {
    if (!this.contentIsAvailable) {
      this.loading = false;
      return;
    }

    this.apiProps = _.merge(
      {
        with: "category,provider,contract_product_counts,provider.logos"
      },
      _.isNull(this.categoryCode)
        ? {}
        : { "filter[category.code]": this.categoryCode }
    );

    this.loadData();
  }

  public beforeDestroy() {
    this.$bus.$off("refreshConfigurationsTable");
  }

  // Methods

  public async loadData() {
    try {
      await Promise.all([
        this.getCategoriesWithProviders(this.isReloading),
        this.getAllProvisionConfigurations(this.isReloading)
      ]);
      await this.getData();

      this.requestError = false;

      this.$bus.$on("refreshConfigurationsTable", () => {
        this.refresh();
      });
    } catch (error) {
      this.requestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.loading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public async refresh() {
    this.getData({ ignoreStored: true });
    this.getAllProvisionConfigurations(true);

    this.$bus.$emit("refreshAvailableConfigurationsCount");
  }

  public deleteProviderConfiguration(
    configurationId: IProvisionConfiguration["id"]
  ) {
    if (!this.canDelete) return;
    this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONFIGURATIONS}/openConfirmDeleteModal`,
      _.merge(
        {
          configurationId,
          onSuccess: this.refresh
        },
        this.deleteMessages
      )
    );
  }

  public async openManageModal(configurationId: IProvisionConfiguration["id"]) {
    this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONFIGURATIONS}/openManageProviderConfigurationModal`,
      {
        props: {
          configurationId,
          actionType: ActionTypes.UPDATE
        },
        events: {
          success: this.refresh
        }
      }
    );
  }

  // Computed

  public get canCreate() {
    return this.$userCan("create_provision_configuration");
  }

  public get canUpdate() {
    return this.$userCan("update_provision_configuration");
  }

  public get canDelete() {
    return this.$userCan("delete_provision_configuration");
  }

  public get deleteMessages() {
    if (this.categoryCode === ProvisionCategoryCodes.SHARED_HOSTING) {
      return {
        message: this.$t("_sentence.delete_web_server_confirmation"),
        successMessage: this.$t("_sentence.web_server_successfully_deleted")
      };
    }

    if (this.categoryCode === ProvisionCategoryCodes.DOMAINS) {
      return {
        message: this.$t("_sentence.delete_registrar_confirmation"),
        successMessage: this.$t("_sentence.registrar_successfully_deleted")
      };
    }

    return {};
  }

  // Guarding

  public guardFunctionalities = ["list_provision_configurations"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Life-cycle Methods

  public render(h: CreateElement) {
    if (!this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading || this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $provisionConfigurationsData: this.providerData(),
        $provisionConfigurationsMethods: this.providerMethods
      })
    );
  }

  // Provide

  @Provide("provisionConfigurationsProviderData")
  public providerData(): IProvisionConfigurationsProviderData {
    return {
      allCategoriesTotal: this.allCategoriesTotal,
      canCreate: this.canCreate,
      canUpdate: this.canUpdate,
      canDelete: this.canDelete,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      enablePagination: this.enablePagination,
      activeFilters: this.activeFilters,
      filters: this.filters,
      sorters: this.sorters,
      table: this.table,
      categoryCode: this.categoryCode,
      loading: this.loading
    };
  }

  @Provide("provisionConfigurationsProviderMethods")
  public get providerMethods(): IProvisionConfigurationsProviderMethods {
    return {
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onSort: this.onSort,
      onPageChange: this.onPageChange,
      onLimitChange: this.onLimitChange,
      refresh: this.refresh,
      deleteProviderConfiguration: this.deleteProviderConfiguration,
      openManageModal: this.openManageModal
    };
  }
}
